import { useTranslation } from 'react-i18next';

import {
  DIFF_STATUS_BACKGROUND_CLASS,
  type DiffStatus,
} from '~/components/common/enrichment-diff-table/diff-status.util';
import { cn } from '~/utils/cn';

import type { CompareField } from './compare-grid.types';

/** Renders one field value with the presentation the host feature supplies (e.g. `EnrichedFieldCell`). */
export type CompareValueRenderer = (
  key: string,
  value: unknown,
  status?: DiffStatus,
) => React.ReactNode;

/**
 * Fades the last few pixels of a clamped box so a cut-off value reads as
 * deliberately truncated rather than broken. On content shorter than the clamp
 * the faded band is empty, so it has no visible effect.
 */
const CLAMP_FADE = 'linear-gradient(to bottom, black calc(100% - 14px), transparent)';

interface ClampedProps {
  /** Fixed content height in px. Every cell shares it, which is what keeps rows aligned. */
  height: number;
  children: React.ReactNode;
  className?: string;
}

/**
 * Locks content to a fixed height and hides the overflow, so one long
 * description can't stretch a row to ten times the height of its neighbours.
 * The full value stays reachable by double-clicking the cell.
 *
 * The height is fixed rather than a max, so every row is exactly the same
 * height without measuring anything — 1,000+ cells and no observers.
 */
export function ClampedContent({ height, children, className }: ClampedProps) {
  return (
    <div
      // shrink-0 so a flex parent can't collapse the fixed height back to content size.
      className={cn('shrink-0 overflow-hidden', className)}
      style={{ height: `${String(height)}px`, maskImage: CLAMP_FADE, WebkitMaskImage: CLAMP_FADE }}
    >
      {children}
    </div>
  );
}

/** Marker chip styling per status — `unchanged` and `modified` carry no chip, the cell tint already says it. */
const MARKER_CLASS: Partial<Record<DiffStatus, string>> = {
  enriched: 'bg-success-50 text-success',
  removed: 'bg-error-50 text-error',
};

/**
 * An operator edit gets its own tint rather than reusing a diff colour: "I
 * changed this" is a different claim from any of the three model outcomes, and
 * collapsing it into `modified` would hide who made the change.
 */
const EDITED_TINT_CLASS = 'bg-info-50';
const EDITED_CHIP_CLASS = 'bg-info-50 text-info';

const MARKER_CHIP_CLASS =
  'inline-flex w-fit items-center rounded px-1 text-[9px] leading-[14px] font-bold tracking-wide uppercase';

/**
 * Short chip naming why a cell stands out: a filled gap, a dropped value, or a
 * local edit.
 *
 * The strip keeps its height even with no chip to show. Without that, a row
 * containing one "New" chip would sit taller than a row of unchanged values and
 * the uniform-height clamp would be undone by the chip itself.
 */
function CellMarker({ field }: { field: CompareField }) {
  const { t } = useTranslation('content-enrichment');

  const chip = field.isEdited ? (
    <span className={cn(MARKER_CHIP_CLASS, EDITED_CHIP_CLASS)} data-testid="cell-marker-edited">
      {t('compareGrid.marker.edited')}
    </span>
  ) : MARKER_CLASS[field.status] ? (
    <span
      className={cn(MARKER_CHIP_CLASS, MARKER_CLASS[field.status])}
      data-testid={`cell-marker-${field.status === 'enriched' ? 'new' : 'removed'}`}
    >
      {t(field.status === 'enriched' ? 'compareGrid.marker.new' : 'compareGrid.marker.removed')}
    </span>
  ) : null;

  return (
    <span data-testid="cell-marker-strip" className="flex h-3.5 items-center">
      {chip}
    </span>
  );
}

interface FeedValueProps {
  value: unknown;
  renderValue: CompareValueRenderer;
  fieldKey: string;
  /** Clamps the value to this pixel height. Omit to let it size naturally (used inside the expanded viewer). */
  clampHeight?: number;
}

/** The feed ("as received") half of a comparison — muted, with an explicit empty state so a gap reads as deliberate. */
export function CompareFeedValue({ value, renderValue, fieldKey, clampHeight }: FeedValueProps) {
  const { t } = useTranslation('content-enrichment');
  const isEmpty = value == null || (Array.isArray(value) && value.length === 0);

  const body = isEmpty ? (
    <p className="text-muted-foreground/60 text-xs italic" data-testid="compare-feed-value-empty">
      {t('compareGrid.cell.noFeedValue')}
    </p>
  ) : (
    <div className="text-muted-foreground" data-testid="compare-feed-value">
      {renderValue(fieldKey, value)}
    </div>
  );

  if (clampHeight === undefined) return body;
  return <ClampedContent height={clampHeight}>{body}</ClampedContent>;
}

interface CompareEnrichedValueProps {
  field: CompareField;
  renderValue: CompareValueRenderer;
  /** Renders the feed value beneath the enriched one — the overlay layout's stacked presentation. */
  showFeedBeneath?: boolean;
  /**
   * True when the renderer already applies its own per-item diff tint (e.g.
   * highlight/detail card lists). The wrapper tint is then skipped so the value
   * isn't shaded twice.
   */
  selfTinted?: boolean;
  /** Clamps the enriched value to this pixel height. Omit inside the expanded viewer, which shows it in full. */
  clampHeight?: number;
  /** Clamps the stacked feed value in overlay layout. Ignored unless `showFeedBeneath`. */
  feedClampHeight?: number;
}

/**
 * The enriched half of a comparison: the value, tinted by diff status, plus a
 * marker chip when it filled a gap, dropped a value, or was locally edited.
 * In overlay layout the feed value is stacked underneath instead of occupying
 * its own column.
 */
export function CompareEnrichedValue({
  field,
  renderValue,
  showFeedBeneath = false,
  selfTinted = false,
  clampHeight,
  feedClampHeight,
}: CompareEnrichedValueProps) {
  const { t } = useTranslation('content-enrichment');
  const isEmpty = field.enrichedValue == null;

  const value = isEmpty ? (
    <span
      className="text-muted-foreground text-xs italic"
      data-testid="compare-enriched-value-empty"
    >
      {t('compareGrid.cell.notSet')}
    </span>
  ) : (
    renderValue(field.key, field.enrichedValue, field.status)
  );

  // The tint hugs its content, and the clamp wraps the tint. The other way round
  // leaves a tall empty tinted band under any short value, which reads as a
  // rendering bug rather than a deliberately compact cell.
  // An edit overrides the diff tint — including for self-tinting card lists,
  // which can't express "edited" through their per-item background.
  const tinted = (
    <div
      data-testid="compare-enriched-value"
      className={cn(
        'rounded px-1.5 py-1',
        field.isEdited
          ? EDITED_TINT_CLASS
          : !selfTinted && DIFF_STATUS_BACKGROUND_CLASS[field.status],
      )}
    >
      {value}
    </div>
  );

  return (
    <div className="flex flex-col gap-1">
      {clampHeight === undefined ? (
        tinted
      ) : (
        <ClampedContent height={clampHeight}>{tinted}</ClampedContent>
      )}
      <CellMarker field={field} />
      {showFeedBeneath && (
        <CompareFeedValue
          value={field.feedValue}
          renderValue={renderValue}
          fieldKey={field.key}
          {...(feedClampHeight !== undefined && { clampHeight: feedClampHeight })}
        />
      )}
    </div>
  );
}
